/**
 * ScenarioEditor
 * 
 * Main scenario configuration page for Site DNA.
 * Uses custom hooks for state management:
 *   - useScenarioForm (form fields)
 *   - useLoadProfile (8760 load data)
 *   - useOptimization (job submission and polling)
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Container, Box } from "@mui/material";

// Custom hooks
import { useScenarioForm, useLoadProfile, useOptimization, useScenarioPayload, useOptimizationSubmit, useFinancialAnalysis, useSystemSizes, useDirtyTracking, useFormValidation, useGraphData, useLoadPreviewData, useCaDerDetection, useMonthlyPerformance, useGeocode, useHashTabNavigation, useTariffSync, useDateDaySync, useSidebarSync, useScenarioLoader, debug, API_BASE_URL, useOptimisticSync, useBudget, useCommercialAssumptions, useCapexRates, useAuth, useThemeMode, useRetroContextSafe, useTariff, buildFinancialSummary, vortxColors, hydrateElectricTariff } from '@vortx/shared-ui';

// Utilities

// Centralized state management hooks

// Sub-components
import { ScenarioFormProvider, createLegacySyncCallback } from '../components/scenario/ScenarioFormProvider';

// Feature sections (extracted from ScenarioEditor)
import {
    SiteSection,
    UtilitySection,
    LoadProfileSection,
    SystemSection,
    FinancialSection,
    AnalysisSection,
    ResultsSection,
} from '../features/scenario/sections';

// Feature components
import {
    OptimizationStatusAlert,
    ScenarioEditorHeader,
    ValidationAlerts,
    ScenarioEditorDialogs,
} from '../features/scenario';

// UX Components
import { SkeletonScenarioEditor } from '../components/ux';

// Utilities and data transformation
import { reoptToDailySeries } from "../reoptTransform";

// Budget and commercial hooks
// Theme

// Styling - extracted to separate file
import { vortxStyles, retroContainerStyles } from './scenarioEditorStyles';

// Common components
import { TabPanel } from '../components/common';

/**
 * Main ScenarioEditor Component
 */
export default function ScenarioEditor() {
    const { projectId, scenarioId: paramScenarioId } = useParams();
    const locationPath = useLocation().pathname;
    const scenarioId = paramScenarioId || (locationPath.endsWith("/new") ? "new" : undefined);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { currentUser } = useAuth();
    const { isDark, isRetro } = useThemeMode();
    const { setProjectContext, scenarioTab, setScenarioTab, setScenarioValidation } = useRetroContextSafe();

    // ========== Custom Hooks ==========
    const scenarioForm = useScenarioForm();
    const loadProfile = useLoadProfile();
    const optimization = useOptimization();
    const { setSelectedTariff } = useTariff();

    // ========== OPTIMISTIC SYNC IMPLEMENTATION ==========
    // Server is source of truth, save on blur/navigation
    // See docs/plans/2026-01-16-optimistic-sync-design.md
    // ====================================================

    // Dirty tracking - extracted to useDirtyTracking hook
    const {
        isDirty,
        captureStateSnapshot,
        lastSavedSnapshot,
        setLastSavedSnapshot,
    } = useDirtyTracking(scenarioForm);

    // Toast notification for remote updates
    const [remoteUpdateToast, setRemoteUpdateToast] = useState(false);

    // ========== Local UI State ==========
    const [schema, setSchema] = useState(null);

    // Date/Day Index synchronization - extracted to useDateDaySync hook
    const {
        selectedDate,
        setSelectedDate,
        dayIndex,
        setDayIndex,
    } = useDateDaySync({
        loadProfile,
        optimizationResults: optimization.results,
    });

    // Resilient Mode State
    const [showResilientMode, setShowResilientMode] = useState(false);
    const [outageStartHour, setOutageStartHour] = useState(14); // Default to 2 PM

    // Breadcrumb navigation state
    const [projectName, setProjectName] = useState('');
    const [projectData, setProjectData] = useState(null);

    // Derived state
    const tph = loadProfile.tph || 1;
    const [submitConfirmation, setSubmitConfirmation] = useState("");
    const [lastRunTimestamp, setLastRunTimestamp] = useState(null); // Timestamp of displayed results
    const [showValidation, setShowValidation] = useState(false);

    // URL-synced main tab state for browser back/refresh support
    const mainTab = parseInt(searchParams.get('tab') || '0');
    const setMainTab = (value) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('tab', value.toString());
            return newParams;
        }, { replace: true });
    };

    // URL-synced sub-tab states for browser back/refresh support
    const resultsSubTab = parseInt(searchParams.get('resultsTab') || '0');
    const setResultsSubTab = (value) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('resultsTab', value.toString());
            return newParams;
        }, { replace: true });
    };
    const utilitySubTab = parseInt(searchParams.get('utilityTab') || '0');
    const setUtilitySubTab = (value) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('utilityTab', value.toString());
            return newParams;
        }, { replace: true });
    };
    const financialSubTab = parseInt(searchParams.get('financialTab') || '0');
    const setFinancialSubTab = (value) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('financialTab', value.toString());
            return newParams;
        }, { replace: true });
    };
    const [loadedBudgetState, setLoadedBudgetState] = useState(null); // Budget state loaded from backend
    const [optimizationEngine, setOptimizationEngine] = useState('vortx_flux'); // Default to Vortx Flux
    const [capexSource, setCapexSource] = useState('custom'); // 'nrel' or 'custom' - default to 'custom' for Vortx Flux solver
    const [includeItc, setIncludeItc] = useState(true); // Include Investment Tax Credit in financial calculations (defaults to true)
    const [selectedBatteryData, setSelectedBatteryData] = useState(null); // Selected batteries array (or null) with cost info
    const [selectedSolarData, setSelectedSolarData] = useState(null); // Selected solar equipment with cost info
    const [selectedGeneratorData, setSelectedGeneratorData] = useState(null); // Selected generator with cost info
    const [selectedSwitchgearData, setSelectedSwitchgearData] = useState(null); // Selected switchgear with cost info

    // Calculate system sizes - prefer optimization results when available, fall back to form inputs
    // Extracted to useSystemSizes hook for maintainability
    const systemSizes = useSystemSizes({ optimization, scenarioForm, loadProfile });

    // Budget hook for CapEx management - must be declared before captureStateSnapshot uses it
    const budget = useBudget({
        systemSizes,
        selectedBatteryData,
        selectedSolarData,
        selectedGeneratorData,
        selectedSwitchgearData,
        initialState: loadedBudgetState
    });

    // Commercial assumptions for financial modeling (analysis years, discount rate, escalation)
    const commercialAssumptions = useCommercialAssumptions(
        scenarioForm.commercialAssumptions
    );

    // Admin-managed CapEx rates for Matrix Builder (system-wide defaults)
    const adminCapexRates = useCapexRates();

    // Monthly performance data - extracted to useMonthlyPerformance hook
    // Lazy-loads when user navigates to Financial/Analysis/Results tabs
    const { monthlyPerformanceData } = useMonthlyPerformance({
        optimization,
        scenarioForm,
        currentTab: scenarioForm.tab,
    });

    // CA DER Detection - extracted to useCaDerDetection hook
    // Runs detection when location/equipment changes, uses tariff utility as source of truth
    useCaDerDetection({ scenarioForm, currentUser });

    // Vortx Flux specific settings
    const [vortxSettings, setVortxSettings] = useState({
        gridImportLimitKw: '',
        gridExportLimitKw: '',
        // Battery Degradation
        batteryCycleCostPerKwh: '0.05',
        batteryMaxCyclesPerYear: '280',
        // Resilience
        minOutageSurvivalHours: '',
        criticalLoadFraction: '0.5',
        // Tiered Rates
        enableTieredRates: false,
        energyTiers: [], // [{ max_usage: 500, rate: 0.15 }, { max_usage: Infinity, rate: 0.25 }]
    });

    // Find minimum limit state
    const [findingMinimumLimit, setFindingMinimumLimit] = useState(false);
    const [minimumLimitResult, setMinimumLimitResult] = useState(null);

    // Cached BAU results for faster optimizations
    const [cachedBauResults, setCachedBauResults] = useState(null);

    // Build scenario payload for optimization (extracted to hook)
    const { buildScenarioPayload } = useScenarioPayload({
        scenarioForm,
        loadProfile,
        optimizationEngine,
        vortxSettings,
        selectedBatteryData,
        budget,
    });

    // ========== Tariff State Sync ==========
    // Extracted to useTariffSync hook for syncing effective tariff to TariffContext
    useTariffSync({
        useManualRate: scenarioForm.useManualRate,
        blendedEnergyRate: scenarioForm.blendedEnergyRate,
        blendedDemandRate: scenarioForm.blendedDemandRate,
        customTariffData: scenarioForm.customTariffData,
        vortxSettings,
        setSelectedTariff,
    });

    // ========== Hash-Based Tab Navigation ==========
    // Extracted to useHashTabNavigation hook for URL sync and browser back/forward support
    const { hasInitializedFromHash } = useHashTabNavigation({
        currentTab: scenarioForm.tab,
        setTab: scenarioForm.setTab,
        sidebarTab: scenarioTab,
        setSidebarTab: setScenarioTab,
    });

    // Geocode zip code when it changes - extracted to useGeocode hook
    useGeocode({
        zipCode: scenarioForm.zipCode,
        currentLocation: scenarioForm.tariffLocation,
        setTariffLocation: scenarioForm.setTariffLocation,
        setSnackbar: scenarioForm.setSnackbar,
    });

    // ========== Required Fields Validation ==========
    // Uses extracted useFormValidation hook for required field checking
    const {
        requiredFields,
        missingFields,
        canProceedToNextTab,
        hasExistingResults,
    } = useFormValidation({
        scenarioForm,
        loadProfile,
        optimizationResults: optimization?.results,
    });

    // ========== Sidebar Context Sync ==========
    // Extracted to useSidebarSync hook for validation state and tab sync
    useSidebarSync({
        requiredFields,
        missingFields,
        setScenarioValidation,
        scenarioTab,
        formTab: scenarioForm.tab,
        setFormTab: scenarioForm.setTab,
        setScenarioTab,
        canProceedToNextTab,
        hasInitializedFromHash,
    });

    // Create validated ITC settings prop
    const itcSettingsForAnalysis = useMemo(() => {
        if (!budget?.itc) {
            return { includeItc: false, inputs: null };
        }

        // Validate structure
        const includeItc = budget.includeItc || false;
        const inputs = includeItc ? budget.itcInputs : null;

        if (includeItc && !inputs) {
            debug.warn('[ScenarioEditor] ITC enabled but inputs missing - disabling ITC');
            return { includeItc: false, inputs: null };
        }

        return { includeItc, inputs };
    }, [budget?.itc, budget?.includeItc, budget?.itcInputs]);

    // Handle tab change with validation
    // Use ref to access latest saveScenarioToBackend in tab handler
    const saveRef = React.useRef(null);
    // Guard against duplicate POST creations during rapid tab changes on new scenarios
    const creatingScenarioRef = useRef(false);

    // Graph data from optimization results (extracted to useGraphData hook)
    const {
        rawGraphData,
        normalGraphData,
        resilientGraphData,
        graphData,
        isOffGrid,
        infeasibilityMetrics,
    } = useGraphData({
        results: optimization.results,
        dayIndex,
        tph,
        loadYear: loadProfile.loadYear,
        showResilientMode,
        outageStartHour,
    });

    // Load preview data
    const {
        loadPreviewDayIndex,
        setLoadPreviewDayIndex,
        peakLoadDay,
        loadPreviewGraphData,
    } = useLoadPreviewData(loadProfile);

    // Wrapper for reoptToDailySeries that includes loadYear for correct timestamps
    const reoptTransformWithYear = useCallback((results, dayIdx = 0, stepsPerHour = 1) => {
        return reoptToDailySeries(results, dayIdx, stepsPerHour, loadProfile.loadYear);
    }, [loadProfile.loadYear]);

    // ========== Auth Headers ==========
    const getHeaders = useCallback(async () => {
        const headers = { 'Content-Type': 'application/json' };
        if (currentUser) {
            try {
                const token = await currentUser.getIdToken();
                headers['Authorization'] = `Bearer ${token}`;
            } catch (e) {
                debug.error('Failed to get ID token:', e);
                throw e;
            }
        } else {
            headers['X-Dev-User-Id'] = 'tim@vortx.us';
        }
        return headers;
    }, [currentUser]);

    // ========== Scenario Loading (extracted to useScenarioLoader) ==========
    // Ref for optimistic sync bridge (optimisticSync defined later in component)
    const optimisticSyncRef = useRef(null);

    const {
        isLoading: isLoadingScenario,
        exists: scenarioExists,
        resultsSavedRef,
        lastSavedRunUuidRef,
        resultsSaveInProgressRef,
        savedFinancialSummaryRef,
        cleanStateSnapshotRef,
        loadedStateRef,
        financialSummarySavedForRunRef,
    } = useScenarioLoader({
        projectId,
        scenarioId,
        getHeaders,
        scenarioForm,
        loadProfile,
        optimization,
        setVortxSettings,
        setProjectContext,
        projectName,
        setLastRunTimestamp,
        setLastSavedSnapshot,
        setLoadedBudgetState,
        onLoaded: ({ updatedAt }) => {
            optimisticSyncRef.current?.markClean(updatedAt);
        },
    });

    // Shared financial analysis - calculated once and used by both Performance and Financials tabs
    // Extracted to useFinancialAnalysis hook for maintainability
    const financialAnalysis = useFinancialAnalysis({
        optimization,
        monthlyPerformanceData,
        isLoadingScenario,
        scenarioId,
        budget,
        commercialAssumptions,
        gasTariffData: scenarioForm.gasTariffData,
    });

    // ========== Load Project Data (Name + Meters) for Breadcrumbs & Load Profiles ==========
    useEffect(() => {
        const fetchProject = async () => {
            if (!projectId) return;
            try {
                const headers = await getHeaders();
                const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}`, { headers });
                if (res.ok) {
                    const data = await res.json();
                    setProjectName(data.name || '');
                    setProjectData(data);
                    // Update retro context for breadcrumb
                    setProjectContext(data, null);
                }
            } catch (e) {
                debug.error('Failed to fetch project data:', e);
            }
        };
        fetchProject();
    }, [projectId, getHeaders, setProjectContext]);

    // ========== Load Base Facility Load from Meter ==========
    useEffect(() => {
        if (!projectData?.meters) return;

        // Determine which meter this app uses
        const appConfig = projectData?.apps?.site_dna;
        const meterAssignment = (Array.isArray(appConfig) ? appConfig[0]?.meter : appConfig?.meter) || 'main';
        const meterLoad = projectData.meters[meterAssignment]?.load_profile;

        if (meterLoad?.loads_kw?.length === 8760) {
            loadProfile.setBaseLoads(meterLoad.loads_kw);
            loadProfile.setLoadYear(String(meterLoad.year || new Date().getFullYear()));
            loadProfile.setLoadSource(meterLoad.source || 'csv_upload');
            loadProfile.setLoadFileName(meterLoad.file_name || '');
        }
    }, [projectData?.meters]); // eslint-disable-line react-hooks/exhaustive-deps

    // ========== Pre-populate New Scenario from Project Data ==========
    useEffect(() => {
        if (scenarioId !== 'new' || !projectData) return;

        const prefillFromProject = async () => {
            // Pre-populate location from unified project
            const loc = projectData.location;
            if (loc) {
                if (loc.address) scenarioForm.setStreet(loc.address);
                if (loc.city) scenarioForm.setCity(loc.city);
                if (loc.state) scenarioForm.setState(loc.state);
                if (loc.zip_code) scenarioForm.setZipCode(loc.zip_code);
            }

            // Pre-populate site name from project name
            if (projectData.name) {
                scenarioForm.setSiteName(projectData.name);
            }

            // Pre-populate utility name and tariff from meter
            const appConfig = projectData?.apps?.site_dna;
            const meterKey = (Array.isArray(appConfig) ? appConfig[0]?.meter : appConfig?.meter) || 'main';
            const meter = projectData.meters?.[meterKey];
            if (meter?.tariff?.utility) {
                scenarioForm.setUtilityName(meter.tariff.utility);
            }

            // Hydrate the full tariff from the project-level meter tariff ID
            if (meter?.tariff?.id) {
                try {
                    const headers = await getHeaders();
                    await hydrateElectricTariff({
                        data: { tariff_label: meter.tariff.id, tariff_data: null },
                        headers,
                        setSelectedTariffData: scenarioForm.setSelectedTariffData,
                        setCustomTariffData: scenarioForm.setCustomTariffData,
                        setVortxSettings,
                    });
                    debug.log('Pre-populated tariff from project meter:', meter.tariff.id);
                } catch (err) {
                    debug.error('Failed to hydrate project tariff:', err);
                }
            }

            // Update dirty tracking baseline so pre-populated values aren't treated as unsaved changes
            setLastSavedSnapshot(JSON.stringify({
                scenarioName: "",
                siteName: projectData.name || "",
                zipCode: loc?.zip_code || "",
                street: loc?.address || "",
                city: loc?.city || "",
                state: loc?.state || "",
                usePv: false,
                pvMaxKw: "",
                pvCost: String(scenarioForm.DEFAULT_PV_COST_PER_KW),
                useStorage: false,
                storageMaxKw: "",
                storageMaxKwh: "",
                useGenerator: false,
                generatorMaxKw: "",
                offGrid: false,
                gasTariffId: null,
                gasTariffData: null,
            }));
        };

        prefillFromProject();
    }, [scenarioId, projectData]); // eslint-disable-line react-hooks/exhaustive-deps

    // ========== Pre-populate New Scenario from Variant (Matrix Builder) ==========
    useEffect(() => {
        if (scenarioId !== 'new') return;

        const prefillKey = searchParams.get('prefillKey');
        if (!prefillKey) return;

        const raw = sessionStorage.getItem(prefillKey);
        if (!raw) {
            console.error(`[ScenarioEditor] prefillKey "${prefillKey}" not found in sessionStorage`);
            return;
        }

        try {
            const prefill = JSON.parse(raw);

            // Scenario name
            if (prefill.name) {
                scenarioForm.setScenarioName(prefill.name);
            }

            // Solar PV
            if (prefill.PV && (prefill.PV.min_kw > 0 || prefill.PV.max_kw > 0)) {
                scenarioForm.setUsePv(true);
                scenarioForm.setSizingMode('prescribed');
                scenarioForm.setPvMaxKw(String(prefill.PV.max_kw || prefill.PV.min_kw));
            }

            // Battery Storage
            if (prefill.ElectricStorage && (prefill.ElectricStorage.min_kwh > 0 || prefill.ElectricStorage.max_kwh > 0)) {
                scenarioForm.setUseStorage(true);
                scenarioForm.setStorageMaxKw(String(prefill.ElectricStorage.max_kw || prefill.ElectricStorage.min_kw));
                scenarioForm.setStorageMaxKwh(String(prefill.ElectricStorage.max_kwh || prefill.ElectricStorage.min_kwh));
            }

            // Generator
            if (prefill.Generator && (prefill.Generator.min_kw > 0 || prefill.Generator.max_kw > 0)) {
                scenarioForm.setUseGenerator(true);
                scenarioForm.setGeneratorMaxKw(String(prefill.Generator.max_kw || prefill.Generator.min_kw));
            }

            // Clean up sessionStorage (one-time use)
            sessionStorage.removeItem(prefillKey);
        } catch (err) {
            console.error('[ScenarioEditor] Failed to parse variant prefill:', err);
        }
    }, [scenarioId, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    // ========== Load Schema ==========
    useEffect(() => {
        const loadSchema = async () => {
            try {
                const headers = await getHeaders();
                headers["X-Frontend-Source"] = "react-frontend";
                const res = await fetch(`${API_BASE_URL}/api/site_dna/schema`, { headers });
                const data = await res.json();
                setSchema(data);
            } catch (e) {
                debug.error("Schema fetch failed", e);
            }
        };
        loadSchema();

        // Load last run from localStorage
        const savedLastRunUuid = localStorage.getItem("lastRunUuid");
        if (savedLastRunUuid) {
            optimization.setLastRunUuid(savedLastRunUuid);
        }
    }, []);

    // ========== Auto-Save Helper ==========
    const saveScenarioToBackend = useCallback(async (signal = null, runUuid = null, resultsData = null) => {
        const saveStartTime = performance.now();
        debug.log('[SAVE] saveScenarioToBackend called', {
            projectId,
            scenarioId,
            isDirty: isDirty,
            hasSignal: !!signal,
        });

        if (!projectId) return;

        // Safeguard: Don't save if scenarioId is invalid
        // This prevents 404 errors when navigating between scenarios
        if (!scenarioId || scenarioId === 'undefined') {
            debug.log('⚠️ Skipping save - invalid scenario ID:', scenarioId);
            return;
        }

        // Safeguard: Don't save if scenario was not found during load (404)
        if (!scenarioExists && scenarioId !== 'new') {
            debug.log('⚠️ Skipping save - scenario does not exist in backend');
            return;
        }

        // Safeguard: Don't save if form appears to not be loaded yet
        // This prevents overwriting valid saved data with empty defaults
        const hasLoadProfile = loadProfile.loads && loadProfile.loads.length === 8760;
        const hasEVLoad = loadProfile.evLoadSource && loadProfile.evLoads && loadProfile.evLoads.length > 0;
        const hasZipCode = scenarioForm.zipCode && scenarioForm.zipCode.trim().length === 5;

        if (!hasZipCode && !hasLoadProfile && !hasEVLoad) {
            debug.log('⚠️ Skipping save - form not properly loaded (no zip code, load profile, or EV load)');
            return;
        }

        // Log the payload for debugging
        debug.log('Battery Config - useStorage:', scenarioForm.useStorage);
        debug.log('Battery Config - storageMaxKw:', scenarioForm.storageMaxKw);
        debug.log('Battery Config - storageMaxKwh:', scenarioForm.storageMaxKwh);
        debug.log('Battery Config - batterySizingMode:', scenarioForm.batterySizingMode);

        try {
            const headers = await getHeaders();
            const uiState = {
                form: scenarioForm.serializeFormState(),
                loadProfile: loadProfile.serializeLoadProfile(),
                budget: budget?.serializeBudget?.() || null,
                commercialAssumptions: commercialAssumptions?.getState?.() || null,
                // Equipment selections (store only IDs for retrieval on load)
                selectedEquipment: {
                    solar: selectedSolarData?.id || null,
                    battery: Array.isArray(selectedBatteryData)
                        ? selectedBatteryData.map(b => ({ id: b.id, qty: b.qty || 1 }))
                        : selectedBatteryData?.id || null,
                    generator: selectedGeneratorData?.id || null,
                    switchgear: selectedSwitchgearData?.id || null
                },
                // Persist computed financial metrics for comparison modal
                // Use computed values if available, otherwise preserve previously saved values
                // This prevents losing financial data when save triggers before recalculation completes
                financialSummary: (() => {
                    const summary = buildFinancialSummary({
                        financialAnalysis,
                        budget,
                        savedFinancialSummary: savedFinancialSummaryRef.current,
                    });
                    // Update the ref for future saves if we computed a complete summary
                    if (financialAnalysis && summary) {
                        savedFinancialSummaryRef.current = summary;
                    }
                    return summary;
                })(),
                // Note: Tab is now handled by URL hash, not ui_state
            };

            // Compute inputs hash from core inputs that affect optimization
            const coreInputs = {
                zipCode: scenarioForm.zipCode,
                loadProfileLength: loadProfile.loads?.length || 0,
                loadProfileSum: loadProfile.loads?.reduce((a, b) => a + b, 0) || 0,
                pvKw: scenarioForm.pvMaxKw,
                storageKw: scenarioForm.useStorage ? scenarioForm.storageMaxKw : 0,
                storageKwh: scenarioForm.useStorage ? scenarioForm.storageMaxKwh : 0,
                tariff: scenarioForm.customTariffData?.id || scenarioForm.blendedRate || 'none',
                offGrid: scenarioForm.offGrid,
            };
            const inputsHash = JSON.stringify(coreInputs);

            // Ensure tariff_data always has name at top level for project list display
            const rawTariffData = scenarioForm.customTariffData || scenarioForm.selectedTariffData || null;
            const tariffDataWithName = rawTariffData ? {
                ...rawTariffData,
                // Explicitly ensure name is at top level (check multiple locations)
                name: rawTariffData.name
                    || rawTariffData.label
                    || rawTariffData.rateName
                    || rawTariffData.rate_data?.name
                    || rawTariffData.rate_data?.rateName
                    || (rawTariffData.utility_name && rawTariffData.rate_schedule_name
                        ? `${rawTariffData.utility_name} ${rawTariffData.rate_schedule_name}`
                        : null),
            } : null;

            const body = {
                name: scenarioForm.scenarioName || 'Untitled Scenario',
                reopt_inputs: buildScenarioPayload(),
                ui_state: uiState,
                inputs_hash: inputsHash,
                // Include tariff metadata for project-level tariff listing
                tariff_label: scenarioForm.customTariffData?.id || null,
                // Save full tariff data with name explicitly at top level
                tariff_data: tariffDataWithName,
                // Include gas tariff metadata
                gas_tariff_id: scenarioForm.gasTariffId || null,
                gas_tariff_data: scenarioForm.gasTariffData || null,
            };

            // DEBUG: Log tariff data being saved
            debug.log('📦 Saving tariff data:', {
                customTariffData: scenarioForm.customTariffData ? { id: scenarioForm.customTariffData.id, name: scenarioForm.customTariffData.name } : null,
                selectedTariffData: scenarioForm.selectedTariffData ? 'present' : null,
                tariff_label: body.tariff_label,
                tariff_data: body.tariff_data ? { id: body.tariff_data.id, name: body.tariff_data.name } : null
            });

            // Include run_uuid and full results if provided (after optimization completes)
            debug.log('saveScenarioToBackend called with runUuid:', runUuid, 'resultsData type:', typeof resultsData, 'resultsData keys:', resultsData ? Object.keys(resultsData) : 'null');

            // Determine UUID to save
            const uuidToSave = runUuid || optimization.lastRunUuid;

            // OPTIMIZATION: Only include results if explicitly provided (solver run completed)
            // Manual saves will NOT include optimization results (resultsData = null)
            let resultsToSave = null;
            const hasNewResults = resultsData !== null && resultsData !== undefined;

            if (hasNewResults) {
                // New results provided - save them (this happens after solver completes)
                resultsToSave = resultsData;
                resultsSaveInProgressRef.current = true;  // Prevent AUTO-SAVE from racing
                debug.log('💾 [OPTIMIZATION] Saving NEW results (solver just completed)');
            } else {
                debug.log('⏭️ [OPTIMIZATION] Manual save - skipping results upload');
            }

            // ALWAYS include last_run_uuid if we have one (even during in-progress runs)
            if (uuidToSave) {
                body.last_run_uuid = uuidToSave;
                debug.log('Including last_run_uuid:', uuidToSave);
            }

            // Only include results if we determined they need saving
            if (resultsToSave) {
                body.last_run_results = resultsToSave;
                // Use actual run completion timestamp from results, fallback to now
                const runTimestamp = resultsToSave.completed_at || resultsToSave.timestamp || new Date().toISOString();
                body.last_run_timestamp = runTimestamp;
                debug.log('📦 [OPTIMIZATION] Including results in save payload, timestamp:', runTimestamp);
            }

            // Determine if creating new or updating existing
            const isNew = !scenarioId || scenarioId === 'new';

            // Guard against duplicate POST creations (rapid tab changes)
            if (isNew && creatingScenarioRef.current) {
                debug.log('⚠️ Skipping save - scenario creation already in progress');
                return;
            }

            const method = isNew ? 'POST' : 'PATCH';
            const url = isNew
                ? `${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios`
                : `${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${scenarioId}`;

            if (isNew) {
                creatingScenarioRef.current = true;
            }

            // Note: No client_updated_at - using optimistic sync (last write wins)

            const fetchOptions = {
                method,
                headers,
                body: JSON.stringify(body),
            };

            // Add abort signal if provided
            if (signal) {
                fetchOptions.signal = signal;
            }

            const res = await fetch(url, fetchOptions);

            if (!res.ok) {
                const errorText = await res.text();
                debug.error(`Auto-save failed with status ${res.status} (${method} to project=${projectId}, scenario=${scenarioId}): ${errorText}`);
                throw new Error(`Save failed: ${res.status}`);
            }

            const savedData = await res.json();

            if (isNew) {
                creatingScenarioRef.current = false;
                // Navigate to the new scenario URL, preserving the current tab hash
                const currentHash = window.location.hash || '';
                navigate(`/projects/${projectId}/scenarios/${savedData.id}${currentHash}`, { replace: true });
            }

            // Mark as saved and update snapshot
            debug.log('📝 [SAVE] Save completed:', {
                updated_at: savedData.updated_at,
                savedDataKeys: Object.keys(savedData)
            });
            const currentSnapshot = captureStateSnapshot();
            cleanStateSnapshotRef.current = currentSnapshot;

            // Mark results as saved if they were included in this save
            if (resultsToSave && uuidToSave) {
                resultsSavedRef.current = true;
                lastSavedRunUuidRef.current = uuidToSave;
                resultsSaveInProgressRef.current = false;  // Allow AUTO-SAVE to proceed
                debug.log('✅ [OPTIMIZATION] Results marked as saved for UUID:', uuidToSave);
            }

            // Update last saved snapshot (state update triggers isDirty recalculation)
            setLastSavedSnapshot(JSON.stringify(currentSnapshot));
            debug.log('✅ [SAVE] Save completed, timestamp:', savedData.updated_at);

            const saveDuration = (performance.now() - saveStartTime).toFixed(2);
            debug.log(`✅ Scenario saved successfully via ${method} - took ${saveDuration}ms`);

            // Return the result for useOptimisticSync
            return { updated_at: savedData.updated_at, id: savedData.id };
        } catch (e) {
            creatingScenarioRef.current = false;  // Clear creation guard on error
            resultsSaveInProgressRef.current = false;  // Clear flag on any error
            if (e.name === 'AbortError') {
                debug.log('🚫 Save aborted (newer save triggered)');
                return null;
            }
            debug.error('❌ Auto-save failed:', e);
            throw e; // Re-throw for useOptimisticSync retry logic
        }
    }, [projectId, scenarioId, scenarioExists, scenarioForm, loadProfile, buildScenarioPayload, getHeaders, navigate, captureStateSnapshot]);

    // ========== Optimistic Sync Integration ==========
    // Uses useOptimisticSync hook for save coordination
    // See docs/plans/2026-01-16-optimistic-sync-design.md

    // Wrapper function for useOptimisticSync to call
    const performSave = useCallback(async (payload, signal) => {
        // payload is ignored here - we build it internally
        return await saveScenarioToBackend(signal);
    }, [saveScenarioToBackend]);

    // Get payload for save (used by useOptimisticSync)
    const getPayloadForSync = useCallback(() => {
        return { /* payload built internally by saveScenarioToBackend */ };
    }, []);

    // Fetch scenario for remote update handling
    const fetchScenarioForSync = useCallback(async () => {
        if (!projectId || !scenarioId || scenarioId === 'new') return null;
        try {
            const headers = await getHeaders();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${scenarioId}`, { headers });
            if (res.ok) {
                return await res.json();
            }
        } catch (e) {
            debug.error('Failed to fetch scenario for sync:', e);
        }
        return null;
    }, [projectId, scenarioId, getHeaders]);

    // Handle remote update from another tab
    const handleRemoteUpdate = useCallback((freshData) => {
        debug.log('[OptimisticSync] Received remote update, reloading state');
        // Reload the scenario data into form state
        if (freshData) {
            // Show toast notification
            setRemoteUpdateToast(true);
            // The page will reload from the fresh data
            window.location.reload();
        }
    }, []);

    // Use optimistic sync hook
    const optimisticSync = useOptimisticSync({
        scenarioId,
        projectId,
        isDirty, // Pass ScenarioEditor's isDirty to the hook
        getPayload: getPayloadForSync,
        saveFn: performSave,
        onRemoteUpdate: handleRemoteUpdate,
        fetchScenario: fetchScenarioForSync,
    });

    // Bridge ref for useScenarioLoader's onLoaded callback
    optimisticSyncRef.current = optimisticSync;

    // Legacy saveTransaction for compatibility with existing code
    const saveTransaction = useCallback(async (runUuid = null, resultsData = null) => {
        return await saveScenarioToBackend(null, runUuid, resultsData);
    }, [saveScenarioToBackend]);

    /**
     * Manual save trigger - now delegates to optimistic sync or direct save
     */
    const saveNow = useCallback(async (triggerSource = 'manual') => {
        const startTime = performance.now();
        debug.log(`[SAVE] Triggered by: ${triggerSource}`, { isDirty });

        if (!isDirty && triggerSource !== 'manual') {
            debug.log('[SAVE] No changes to save');
            return;
        }

        await saveTransaction();
        const duration = (performance.now() - startTime).toFixed(2);
        debug.log(`[SAVE] Save completed - took ${duration}ms (trigger: ${triggerSource})`);
    }, [isDirty, saveTransaction]);

    // ========== Form Blur Handler for Optimistic Sync ==========
    // Triggers debounced save when user moves away from form fields
    const handleFormBlur = useCallback((e) => {
        // Only trigger if the blur is moving outside the form container
        // (not just moving between fields within the form)
        const relatedTarget = e.relatedTarget;
        const currentTarget = e.currentTarget;

        // If the new focus is still within the form, don't save yet
        if (relatedTarget && currentTarget.contains(relatedTarget)) {
            return;
        }

        // Mark dirty and trigger debounced save
        if (isDirty) {
            optimisticSync.debouncedSave();
        }
    }, [isDirty, optimisticSync]);

    // ========== Tab Change Handler with Navigation Autosave ==========
    const handleTabChange = useCallback((e, newTab) => {
        const previousTab = scenarioForm.tab;

        // Validation: If moving from Input tabs (0, 1, 2) to Design (3) or Results (4+)
        // Tab 0 (Site), Tab 1 (Utility), Tab 2 (Load) are flexible. Moving to 3+ requires either:
        // 1. All required fields to be filled (for new runs), OR
        // 2. Existing optimization results (allows viewing previous results even with new validation rules)
        if (newTab > 2 && !canProceedToNextTab) {
            setShowValidation(true);
            scenarioForm.setSnackbar({
                open: true,
                message: `Please fill in required fields: ${missingFields.map(f => f.label).join(', ')}`,
                severity: "warning",
            });
            return;
        }

        // Switch to new tab immediately (don't wait for save)
        scenarioForm.setTab(newTab);

        // Navigation-based autosave: trigger save on every tab change
        // saveNow handles dirty check internally and cancels any in-flight saves
        // Also fires for new scenarios — saveScenarioToBackend handles POST creation
        if (projectId && scenarioId) {
            saveNow(`tab-change: ${previousTab} → ${newTab}`).catch((error) => {
                debug.error('[AUTOSAVE] Failed to save on tab change:', error);
            });
        }
    }, [scenarioForm, canProceedToNextTab, missingFields, projectId, scenarioId, saveNow]);

    // Update ref for navigation-based auto-save (stores latest saveNow function)
    const saveNowRef = useRef(null);
    React.useEffect(() => {
        saveNowRef.current = saveNow;
        saveRef.current = saveScenarioToBackend; // Keep for backwards compatibility
    }, [saveNow, saveScenarioToBackend]);

    // Auto-save when financial analysis becomes available after optimization
    // This ensures the comparison modal gets updated financial metrics without manual save
    React.useEffect(() => {
        // Only trigger if:
        // 1. We have optimization results
        // 2. Financial analysis is now available (was calculating)
        // 3. We haven't already saved for this run UUID
        // 4. No results-save is currently in progress (prevents race condition)
        const currentRunUuid = optimization?.runUuid || optimization?.lastRunUuid;

        if (
            financialAnalysis &&
            currentRunUuid &&
            financialSummarySavedForRunRef.current !== currentRunUuid &&
            scenarioId &&
            scenarioId !== 'new' &&
            !resultsSaveInProgressRef.current
        ) {
            debug.log('📊 [AUTO-SAVE] Financial analysis ready, triggering save for comparison modal');
            financialSummarySavedForRunRef.current = currentRunUuid;

            // Trigger save after a short delay to let React settle
            const timeoutId = setTimeout(() => {
                if (saveRef.current) {
                    saveRef.current();
                }
            }, 500);

            return () => clearTimeout(timeoutId);
        }
    }, [financialAnalysis, optimization?.runUuid, optimization?.lastRunUuid, scenarioId]);

    // Navigation-based autosave: trigger on component unmount (navigation away from scenario)
    // Note: Excluded for new scenarios because the navigate() in saveScenarioToBackend
    // would redirect the user back to the editor, overriding their navigation intent
    React.useEffect(() => {
        return () => {
            if (saveNowRef.current && projectId && scenarioId && scenarioId !== 'new') {
                debug.log('[AUTOSAVE] Component unmounting - triggering navigation autosave');
                saveNowRef.current('component-unmount');
            }
        };
    }, [projectId, scenarioId]);

    // ========== Submit Optimization (extracted to hook) ==========
    const { handleSubmit } = useOptimizationSubmit({
        scenarioForm,
        loadProfile,
        missingFields,
        optimization,
        buildScenarioPayload,
        saveTransaction,
        optimizationEngine,
        cachedBauResults,
        setCachedBauResults,
        projectId,
        scenarioId,
        getHeaders,
        resultsSavedRef,
        optimisticSync,
        reoptTransformWithYear,
        setSubmitConfirmation,
        setLastRunTimestamp,
    });

    // ========== Save Scenario ==========
    const handleSaveScenario = useCallback(async () => {
        if (!projectId || !scenarioId) return;

        try {
            // Use saveTransaction which properly handles:
            // - Conflict detection with timestamps
            // - Budget state serialization
            // - Cancellation of in-flight saves
            // NOTE: Pass null for runUuid/resultsData - manual saves don't include results
            const result = await saveTransaction(null, null);

            if (!result) {
                // Save was silently skipped by guards (no zip code or load profile)
                scenarioForm.setSnackbar({
                    open: true,
                    message: "Please enter a zip code or upload a load profile before saving.",
                    severity: "warning",
                });
                return;
            }

            // Update the save status indicator with the new timestamp
            if (result?.updated_at) {
                optimisticSync.markClean(result.updated_at);
            }

            scenarioForm.setSnackbar({
                open: true,
                message: "Scenario saved successfully!",
                severity: "success",
            });
        } catch (e) {
            scenarioForm.setSnackbar({
                open: true,
                message: `Failed to save scenario: ${e.message}`,
                severity: "error",
            });
        }
    }, [projectId, scenarioId, scenarioForm, saveTransaction, optimisticSync]);

    // ========== Delete Scenario ==========
    const handleDeleteScenario = useCallback(async () => {
        if (!projectId || !scenarioId || scenarioId === 'new') return;

        try {
            const headers = await getHeaders();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${scenarioId}`, {
                method: 'DELETE',
                headers,
            });

            if (res.ok) {
                navigate(`/projects/${projectId}/analysis`);
            }
        } catch (e) {
            scenarioForm.setSnackbar({
                open: true,
                message: `Failed to delete scenario: ${e.message}`,
                severity: "error",
            });
        }
    }, [projectId, scenarioId, getHeaders, navigate]);

    // ========== Render ==========

    // Create sync callback for bidirectional form state sync
    // NOTE: Must be above the loading gate to maintain consistent hook count across renders
    const syncToLegacy = useMemo(() => createLegacySyncCallback(scenarioForm), [scenarioForm]);

    // Global loading gate - show skeleton loading UI until scenario data is ready
    // Uses content-shaped skeleton to prevent layout shift and improve perceived performance
    // Note: Don't apply retro styles here to avoid dark overlay flash
    if (isLoadingScenario && scenarioId !== 'new') {
        return <SkeletonScenarioEditor />;
    }

    return (
        <ScenarioFormProvider legacyFormState={scenarioForm} syncToLegacy={syncToLegacy}>
        <Container maxWidth="xl" sx={{ py: isRetro ? 0 : 4, px: isRetro ? 0 : undefined, ...(isRetro && retroContainerStyles) }} onBlur={handleFormBlur}>
            <ScenarioEditorHeader
                isRetro={isRetro}
                projectName={projectName}
                scenarioName={scenarioForm.scenarioName}
                onNavigateBack={() => navigate(`/projects/${projectId}/analysis`)}
                onRename={() => {
                    scenarioForm.setTempScenarioName(scenarioForm.scenarioName);
                    scenarioForm.setRenameDialogOpen(true);
                }}
                isDirty={isDirty}
                saveStatus={optimisticSync.saveStatus}
                saveError={optimisticSync.saveError}
                lastSavedAt={optimisticSync.lastSavedAt}
                onSave={handleSaveScenario}
                optimizationEngine={optimizationEngine}
                onEngineChange={setOptimizationEngine}
                onSubmit={handleSubmit}
                isSubmitting={optimization.isSubmitting}
                missingFields={missingFields}
                vortxStyles={vortxStyles}
            />

            {/* Status */}
            <OptimizationStatusAlert
                optimization={optimization}
                lastRunTimestamp={lastRunTimestamp}
                findingMinimumLimit={findingMinimumLimit}
                minimumLimitResult={minimumLimitResult}
                onFindMinimumLimit={async () => {
                    setFindingMinimumLimit(true);
                    setMinimumLimitResult(null);
                    try {
                        const scenario = buildScenarioPayload();
                        const fmlHeaders = await getHeaders();
                        const response = await fetch(`${API_BASE_URL}/api/site_dna/find-minimum-limit`, {
                            method: 'POST',
                            headers: fmlHeaders,
                            body: JSON.stringify(scenario)
                        });
                        const result = await response.json();
                        if (response.ok) {
                            setMinimumLimitResult(result);
                        } else {
                            setMinimumLimitResult({ error: result.error || 'Search failed' });
                        }
                    } catch (err) {
                        setMinimumLimitResult({ error: err.message });
                    } finally {
                        setFindingMinimumLimit(false);
                    }
                }}
                onApplyMinimumLimit={(limitKw) => {
                    setVortxSettings(prev => ({
                        ...prev,
                        gridImportLimitKw: limitKw?.toString() || ''
                    }));
                }}
                onClearMinimumResult={() => setMinimumLimitResult(null)}
            />

            <ValidationAlerts
                showValidation={showValidation}
                missingFields={missingFields}
                currentTab={scenarioForm.tab}
                needsRerun={optimization.needsRerun}
                hasResults={!!optimization.results}
                onDismissRerun={() => optimization.setNeedsRerun(false)}
            />

            {/* Prevent flicker during initial mount: hide tabs until context syncs */}
            {!(scenarioTab === 0 && scenarioForm.tab > 0) && (
            <>
            {/* Tab 0: Site */}
            <TabPanel value={scenarioForm.tab} index={0}>
                <SiteSection
                    vortxStyles={vortxStyles}
                    showValidation={showValidation}
                    missingFields={missingFields}
                />
            </TabPanel>

            {/* Tab 1: Utility */}
            <TabPanel value={scenarioForm.tab} index={1}>
                <UtilitySection
                    scenarioForm={scenarioForm}
                    vortxSettings={vortxSettings}
                    setVortxSettings={setVortxSettings}
                    optimizationEngine={optimizationEngine}
                    projectId={projectId}
                    vortxStyles={vortxStyles}
                    showValidation={showValidation}
                    requiredTariff={requiredFields.tariff.value}
                    subTab={utilitySubTab}
                    setSubTab={setUtilitySubTab}
                />
            </TabPanel>



            {/* Tab 2: Load Modeling */}
            <TabPanel value={scenarioForm.tab} index={2}>
                <LoadProfileSection
                    loadProfile={loadProfile}
                    tariffLocation={scenarioForm.tariffLocation}
                    onSnackbar={scenarioForm.setSnackbar}
                    loadPreviewDayIndex={loadPreviewDayIndex}
                    setLoadPreviewDayIndex={setLoadPreviewDayIndex}
                    peakLoadDay={peakLoadDay}
                    loadPreviewGraphData={loadPreviewGraphData}
                    isDark={isDark}
                />
            </TabPanel>

            {/* Tab 3: Microgrid Design */}
            <TabPanel value={scenarioForm.tab} index={3}>
                <SystemSection
                    optimizationEngine={optimizationEngine}
                    vortxSettings={vortxSettings}
                    setVortxSettings={setVortxSettings}
                    vortxStyles={vortxStyles}
                    useGenLoadBank={scenarioForm.useGenLoadBank}
                    setUseGenLoadBank={scenarioForm.setUseGenLoadBank}
                    onBatterySelect={setSelectedBatteryData}
                />
            </TabPanel>

            {/* Tab 4: Financial (Budget & Commercial sub-tabs) */}
            <TabPanel value={scenarioForm.tab} index={4}>
                <FinancialSection
                    systemSizes={systemSizes}
                    budget={budget}
                    scenarioForm={scenarioForm}
                    commercialAssumptions={commercialAssumptions}
                    selectedEquipment={{
                        selectedSolarData,
                        setSelectedSolarData,
                        selectedBatteryData,
                        setSelectedBatteryData,
                        selectedGeneratorData,
                        setSelectedGeneratorData,
                        selectedSwitchgearData,
                        setSelectedSwitchgearData,
                    }}
                    subTab={financialSubTab}
                    setSubTab={setFinancialSubTab}
                />
            </TabPanel>

            {/* Tab 5: Scenario Analysis */}
            <TabPanel value={scenarioForm.tab} index={5}>
                <AnalysisSection
                    projectId={projectId}
                    scenarioId={scenarioId}
                    scenarioForm={scenarioForm}
                    optimization={optimization}
                    buildScenarioPayload={buildScenarioPayload}
                    budget={budget}
                    commercialAssumptions={commercialAssumptions}
                    itcSettings={itcSettingsForAnalysis}
                    mainLoadProfile={loadProfile?.loads}
                    monthlyPerformanceData={monthlyPerformanceData}
                    adminCapexRates={adminCapexRates}
                />
            </TabPanel>

            {/* Tab 6: Results (with Performance and Financials sub-tabs) */}
            <TabPanel value={scenarioForm.tab} index={6}>
                <ResultsSection
                    isLoadingScenario={isLoadingScenario}
                    optimization={optimization}
                    scenarioForm={scenarioForm}
                    loadProfile={loadProfile}
                    financialAnalysis={financialAnalysis}
                    capexSource={capexSource}
                    monthlyPerformanceData={monthlyPerformanceData}
                    optimizationEngine={optimizationEngine}
                    vortxStyles={vortxStyles}
                    graphData={graphData}
                    tph={tph}
                    isDark={isDark}
                    budget={budget}
                    systemSizes={systemSizes}
                    selectedEquipment={{
                        selectedSolarData,
                        setSelectedSolarData,
                        selectedBatteryData,
                        setSelectedBatteryData,
                        selectedGeneratorData,
                        setSelectedGeneratorData,
                        selectedSwitchgearData,
                        setSelectedSwitchgearData,
                    }}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    dayIndex={dayIndex}
                    setDayIndex={setDayIndex}
                    showResilientMode={showResilientMode}
                    setShowResilientMode={setShowResilientMode}
                    outageStartHour={outageStartHour}
                    setOutageStartHour={setOutageStartHour}
                    subTab={resultsSubTab}
                    setSubTab={setResultsSubTab}
                    projectId={projectId}
                    scenarioId={scenarioId}
                />
            </TabPanel>
            </>
            )}

            <ScenarioEditorDialogs
                renameDialogOpen={scenarioForm.renameDialogOpen}
                onCloseRenameDialog={() => scenarioForm.setRenameDialogOpen(false)}
                tempScenarioName={scenarioForm.tempScenarioName}
                onTempNameChange={scenarioForm.setTempScenarioName}
                onSaveRename={() => {
                    scenarioForm.setScenarioName(scenarioForm.tempScenarioName);
                    scenarioForm.setRenameDialogOpen(false);
                }}
                deleteDialogOpen={scenarioForm.deleteDialogOpen}
                onCloseDeleteDialog={() => scenarioForm.setDeleteDialogOpen(false)}
                onDelete={handleDeleteScenario}
                remoteUpdateToast={remoteUpdateToast}
                onCloseRemoteToast={() => setRemoteUpdateToast(false)}
                snackbar={scenarioForm.snackbar}
                onCloseSnackbar={() => scenarioForm.setSnackbar({ ...scenarioForm.snackbar, open: false })}
            />
        </Container >
        </ScenarioFormProvider>
    );
}
